import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Heart, Users, ArrowRight, CreditCard, QrCode } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CASHAPP_QR = "https://customer-assets.emergentagent.com/job_vet-impact-platform/artifacts/mq7wtatl_%24Badcompanyco-CASH%20APP%20QR.svg";
const VENMO_URL = "https://venmo.com/code?user_id=4592887459218935610&created=1778622181.1808228&printed=1";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.15, duration: 0.7, ease: "easeOut" } })
};

function fixUrl(url) {
  if (!url) return "";
  if (url.includes("/api/uploads/")) return "/api/uploads/" + url.split("/api/uploads/").pop();
  return url;
}

function DonateModal({ profile, onClose }) {
  const [amount, setAmount] = useState("");
  const [form, setForm] = useState({ name: "", email: "" });
  const [tab, setTab] = useState("cashapp");
  const [submitting, setSubmitting] = useState(false);

  const handleDonate = async () => {
    if (!amount || !form.name || !form.email) { toast.error("Please fill in all fields"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/veteran-profiles/${profile.id}/donate?amount=${amount}&donor_name=${encodeURIComponent(form.name)}&donor_email=${encodeURIComponent(form.email)}`);
      toast.success("Thank you for sponsoring this veteran!");
      onClose();
    } catch { toast.error("Failed"); }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} onClick={e => e.stopPropagation()}
        className="w-full max-w-md border border-white/15 bg-zinc-950 p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="font-subheading text-xl uppercase tracking-wider mb-1">Sponsor {profile.name}</h3>
        <p className="font-body text-xs text-zinc-500 mb-6">{profile.branch}</p>

        {/* Amount */}
        <div className="grid grid-cols-4 gap-2 mb-4">
          {[25, 50, 100, 250].map(a => (
            <button key={a} onClick={() => setAmount(a.toString())}
              className={`py-2 border font-heading text-lg transition-all ${amount === a.toString() ? "border-white bg-white/10" : "border-white/10 text-zinc-500"}`}>
              ${a}
            </button>
          ))}
        </div>
        <input type="number" value={amount} onChange={e => setAmount(e.target.value)} className="bc-input w-full mb-4" placeholder="Custom amount" />

        <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="bc-input w-full mb-3" placeholder="Your name" />
        <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="bc-input w-full mb-4" placeholder="Email" />

        {/* Payment tabs */}
        <div className="flex gap-2 mb-4">
          {[{id:"cashapp",label:"Cash App"},{id:"venmo",label:"Venmo"}].map(m => (
            <button key={m.id} onClick={() => setTab(m.id)}
              className={`flex-1 font-subheading text-xs uppercase py-2 border transition-all ${tab === m.id ? "border-white text-white" : "border-white/10 text-zinc-500"}`}>
              {m.label}
            </button>
          ))}
        </div>

        {tab === "cashapp" && (
          <div className="text-center border border-white/10 p-4 mb-4">
            <img src={CASHAPP_QR} alt="Cash App" className="w-32 h-32 mx-auto mb-2 bg-white p-1" />
            <p className="font-body text-xs text-zinc-500">$Badcompanyco</p>
          </div>
        )}
        {tab === "venmo" && (
          <div className="text-center border border-white/10 p-4 mb-4">
            <a href={VENMO_URL} target="_blank" rel="noopener noreferrer" className="font-subheading text-sm uppercase tracking-widest border border-white/20 text-white px-6 py-2 inline-block hover:border-white transition-colors">Open Venmo</a>
          </div>
        )}

        <button onClick={handleDonate} disabled={submitting}
          className="w-full font-subheading text-sm uppercase tracking-widest bg-white text-black px-6 py-3 hover:bg-zinc-200 transition-colors disabled:opacity-50">
          {submitting ? "Processing..." : "I've Sent Payment - Confirm Sponsorship"}
        </button>
        <button onClick={onClose} className="w-full font-body text-xs text-zinc-500 mt-3 py-2">Cancel</button>
      </motion.div>
    </div>
  );
}

export default function SponsorVeteranPage() {
  const [profiles, setProfiles] = useState([]);
  const [donateProfile, setDonateProfile] = useState(null);

  useEffect(() => {
    axios.get(`${API}/veteran-profiles`).then(r => setProfiles(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="sponsor-veteran-page">
      {/* Hero */}
      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/34730256/pexels-photo-34730256.jpeg?auto=compress&cs=tinysrgb&w=1600" alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-black/70" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={0}>
            <p className="font-body text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-4">Make a Difference</p>
          </motion.div>
          <motion.h1 initial="hidden" animate="visible" variants={fadeUp} custom={1}
            className="font-heading text-5xl sm:text-6xl lg:text-[5.5rem] uppercase tracking-tighter leading-[0.9] mb-6">
            Sponsor a Veteran
          </motion.h1>
          <motion.p initial="hidden" animate="visible" variants={fadeUp} custom={2}
            className="font-body text-base lg:text-lg text-zinc-400 max-w-xl leading-relaxed">
            Choose a veteran to sponsor directly. 100% of your contribution goes toward creating an unforgettable experience for them.
          </motion.p>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-zinc-950 border-b border-white/5">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
            {[
              { num: "01", title: "Choose a Veteran", desc: "Browse profiles and find someone whose story resonates with you" },
              { num: "02", title: "Sponsor Their Experience", desc: "Contribute any amount — every dollar goes directly to their experience" },
              { num: "03", title: "Watch Their Story", desc: "See the impact of your sponsorship through video content" },
            ].map(step => (
              <div key={step.num} className="border border-white/10 p-8">
                <span className="font-heading text-4xl text-white/10">{step.num}</span>
                <h3 className="font-subheading text-sm uppercase tracking-wider mt-3 mb-2">{step.title}</h3>
                <p className="font-body text-xs text-zinc-500">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Veteran Profiles */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="font-subheading text-3xl uppercase tracking-tight mb-10 flex items-center gap-3">
            <Users size={24} className="text-zinc-400" /> Veterans Seeking Sponsors
          </h2>

          {profiles.length === 0 ? (
            <div className="text-center py-20 border border-white/10">
              <Heart size={36} className="text-zinc-600 mx-auto mb-4" />
              <p className="font-subheading text-lg uppercase tracking-wider text-zinc-500 mb-2">Profiles Coming Soon</p>
              <p className="font-body text-sm text-zinc-600 mb-6">We're preparing veteran profiles for sponsorship. Check back soon.</p>
              <Link to="/get-involved" className="font-subheading text-xs uppercase tracking-widest border border-white/20 text-white px-6 py-2.5 hover:border-white transition-colors inline-flex items-center gap-2">
                Donate to General Fund <ArrowRight size={12} />
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {profiles.map((p, i) => {
                const progress = p.goal_amount > 0 ? Math.min(100, (p.raised_amount / p.goal_amount) * 100) : 0;
                return (
                  <motion.div key={p.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1, duration: 0.5 }}
                    className="border border-white/10 overflow-hidden card-hover" data-testid={`veteran-profile-${i}`}>
                    {p.image_url && (
                      <div className="aspect-square overflow-hidden">
                        <img src={fixUrl(p.image_url)} alt={p.name} className="w-full h-full object-cover" />
                      </div>
                    )}
                    {p.video_url && (
                      <div className="aspect-video">
                        <iframe src={p.video_url.includes("youtube.com/watch") ? `https://www.youtube.com/embed/${p.video_url.split("v=")[1]?.split("&")[0]}` : p.video_url}
                          className="w-full h-full" allowFullScreen loading="lazy" title={p.name} />
                      </div>
                    )}
                    <div className="p-6">
                      <h3 className="font-subheading text-xl uppercase tracking-wider mb-1">{p.name}</h3>
                      {p.branch && <p className="font-body text-xs text-zinc-500 uppercase tracking-wider mb-3">{p.branch}</p>}
                      <p className="font-body text-sm text-zinc-400 mb-4 line-clamp-4">{p.story}</p>

                      {p.goal_amount > 0 && (
                        <div className="mb-4">
                          <div className="flex justify-between font-body text-xs text-zinc-500 mb-1">
                            <span>${p.raised_amount?.toLocaleString() || 0} raised</span>
                            <span>${p.goal_amount?.toLocaleString()} goal</span>
                          </div>
                          <div className="w-full h-1.5 bg-zinc-800">
                            <div className="h-full bg-white transition-all duration-500" style={{ width: `${progress}%` }} />
                          </div>
                        </div>
                      )}

                      <button onClick={() => setDonateProfile(p)} data-testid={`sponsor-btn-${i}`}
                        className="w-full font-subheading text-xs uppercase tracking-widest bg-white text-black px-6 py-2.5 hover:bg-zinc-200 transition-colors flex items-center justify-center gap-2">
                        <Heart size={14} /> Sponsor {p.name.split(" ")[0]}
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* General Donation */}
          <div className="mt-16 border border-white/10 p-8 text-center">
            <h3 className="font-subheading text-xl uppercase tracking-wider mb-3">Donate to the General Fund</h3>
            <p className="font-body text-sm text-zinc-500 mb-6 max-w-lg mx-auto">
              Can't decide? Contribute to our general fund and we'll direct it where it's needed most.
            </p>
            <Link to="/get-involved" className="font-subheading text-xs uppercase tracking-widest bg-white text-black px-8 py-3 hover:bg-zinc-200 transition-colors inline-flex items-center gap-2">
              <Heart size={14} /> Donate Now
            </Link>
          </div>
        </div>
      </section>

      {donateProfile && <DonateModal profile={donateProfile} onClose={() => setDonateProfile(null)} />}
    </div>
  );
}
